// Mode B's two calls: clear a category's values on one project, and put a
// cleared batch back.
//
// This hook is the plumbing only. The rule that a destructive act is believed
// only on a decoded count lives in ./clearResultDecode.ts, where a Deno test
// can reach it, and every branch below goes through it. Nothing here reports
// success without a count, and nothing here pre-judges a refusal: the database
// decides who may call these, checking organisation admin AND
// profiles.can_view_financials inside both functions, and this module's job is
// to state the answer in the words the user needs.
//
// It deliberately does NOT import projectPnlView. It moves value rows and
// archive rows and knows nothing about the display overlay. Both RPCs update
// pnl_view themselves, inside the same transaction as the data change, which
// is what stops a cleared category keeping a row or a restored one losing one.
//
// The narrow client type below exists for the same reason src/lib/claimTimingDb.ts
// and src/lib/pnl/analyticsClient.ts do: database.types.ts is generated from the
// live schema and cannot describe 20260915000300, which is not applied. Remove
// it when those types are regenerated.

import { useCallback, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  decodeClearResult,
  decodeRestoreResult,
  describeClearRefusal,
  describeRestoreRefusal,
  type ClearResult,
  type RestoreResult,
  type RpcRefusal,
} from "./clearResultDecode";

export type ClearOutcome =
  | { ok: true; result: ClearResult }
  | { ok: false; message: string; stale: boolean };

export type RestoreOutcome =
  | { ok: true; result: RestoreResult }
  | { ok: false; message: string };

/** The slice of SupabaseClient this module calls, named by function rather than reached through `any`. */
interface PnlClearRpcClient {
  rpc(
    fn: "pnl_clear_project_category",
    args: { p_project_id: string; p_category: string; p_expected_row_ids: string[]; p_reason: string },
  ): PromiseLike<{ data: unknown; error: RpcRefusal | null }>;
  rpc(
    fn: "pnl_restore_value_archive",
    args: { p_batch_id: string },
  ): PromiseLike<{ data: unknown; error: RpcRefusal | null }>;
  rpc(
    fn: "pnl_project_cleared_category_count",
    args: { p_project_id: string },
  ): PromiseLike<{ data: unknown; error: RpcRefusal | null }>;
}

export interface ClearCategoryInput {
  projectId: string;
  category: string;
  /** Every row id the dialog listed, so the RPC refuses with 40001 if the set moved. */
  expectedRowIds: string[];
  reason?: string;
}

/**
 * How many categories currently hold cleared values on one project.
 *
 * Null means the question could not be answered, and the caller must render
 * nothing rather than a zero. The two cases that produce it are a caller
 * without financial access and a database where 20260915000300 is not applied,
 * and in both of them "0 categories cleared" would be a statement about money
 * that nothing established.
 */
export type ClearedCountOutcome = { count: number | null };

export interface UseClearProjectCategoryReturn {
  clearing: boolean;
  restoring: boolean;
  clearCategory: (input: ClearCategoryInput) => Promise<ClearOutcome>;
  restoreBatch: (batchId: string) => Promise<RestoreOutcome>;
  clearedCategoryCount: (projectId: string) => Promise<ClearedCountOutcome>;
}

export function useClearProjectCategory(): UseClearProjectCategoryReturn {
  const [clearing, setClearing] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const clearCategory = useCallback(async (input: ClearCategoryInput): Promise<ClearOutcome> => {
    setClearing(true);
    const client = supabase as unknown as PnlClearRpcClient;
    const { data, error } = await client.rpc("pnl_clear_project_category", {
      p_project_id: input.projectId,
      p_category: input.category,
      p_expected_row_ids: input.expectedRowIds,
      p_reason: input.reason ?? "mode_b_clear",
    });
    setClearing(false);

    if (error) {
      const refusal = describeClearRefusal(error);
      return { ok: false, message: refusal.message, stale: refusal.stale };
    }
    try {
      return { ok: true, result: decodeClearResult(data) };
    } catch (decodeError) {
      // The transaction may well have committed. Saying exactly that is the
      // honest answer: claiming failure would send someone looking for money
      // already sitting in the archive, and claiming success would print
      // counts that nothing returned.
      return {
        ok: false,
        stale: false,
        message:
          `${decodeError instanceof Error ? decodeError.message : String(decodeError)}. ` +
          "Reload the P&L before acting again, and check the cleared values panel in P&L Analytics.",
      };
    }
  }, []);

  const restoreBatch = useCallback(async (batchId: string): Promise<RestoreOutcome> => {
    setRestoring(true);
    const client = supabase as unknown as PnlClearRpcClient;
    const { data, error } = await client.rpc("pnl_restore_value_archive", { p_batch_id: batchId });
    setRestoring(false);

    if (error) return { ok: false, message: describeRestoreRefusal(error) };
    try {
      return { ok: true, result: decodeRestoreResult(data) };
    } catch (decodeError) {
      return {
        ok: false,
        message:
          `${decodeError instanceof Error ? decodeError.message : String(decodeError)}. ` +
          "Check the cleared values panel in P&L Analytics before trying again.",
      };
    }
  }, []);

  /**
   * The count-only surface, and the one call here that a non-admin may make.
   *
   * Gated inside the database on organisation membership AND
   * can_view_financials, with no admin role, because six of this organisation's
   * members are shown every figure on the P&L without one. They cannot read the
   * archive table (its SELECT policy is the admin pair), which is exactly why
   * this is a definer function and not a client-side count of rows they would
   * never be sent.
   */
  const clearedCategoryCount = useCallback(async (projectId: string): Promise<ClearedCountOutcome> => {
    const client = supabase as unknown as PnlClearRpcClient;
    const { data, error } = await client.rpc("pnl_project_cleared_category_count", {
      p_project_id: projectId,
    });
    if (error) return { count: null };
    const parsed = typeof data === "number" ? data : typeof data === "string" ? Number(data) : NaN;
    // Not a number is not a zero. A refusal and an unanswered question both
    // render as nothing, never as "none were cleared".
    return { count: Number.isFinite(parsed) ? parsed : null };
  }, []);

  return { clearing, restoring, clearCategory, restoreBatch, clearedCategoryCount };
}
